import React, { useState, useRef } from 'react';
import { X, Upload, FileText, AlertTriangle, CheckCircle, Download } from 'lucide-react';
import { manualDrawApi } from '../../../../services/api';

interface CSVImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportSuccess: () => void;
}

const CSVImportModal: React.FC<CSVImportModalProps> = ({ isOpen, onClose, onImportSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        setError('Por favor, selecione um arquivo CSV');
        return;
      }
      setFile(selectedFile);
      setError(null);
      setResult(null);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (!droppedFile.name.endsWith('.csv')) {
        setError('Por favor, selecione um arquivo CSV');
        return;
      }
      setFile(droppedFile);
      setError(null);
      setResult(null);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Selecione um arquivo CSV');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      const response = await manualDrawApi.importLeadsFromCSV(file);
      setResult(response);

      // Auto-close and refresh after success
      setTimeout(() => {
        onImportSuccess();
        handleClose();
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Erro ao importar arquivo CSV');
      setResult(null);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setError(null);
    setResult(null);
    onClose();
  };

  const downloadTemplate = () => {
    const csvContent = 'nome,whatsapp,data_nascimento\nJoão Silva,11999999999,01/01/1990\nMaria Santos,11988888888,15/05/1985';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'template_importacao_leads.csv';
    link.click();
  };

  return (
    <div className="fixed inset-0 bg-tinta/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl border border-tinta/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-tinta/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-bordo/8 border border-bordo/20 rounded-full flex items-center justify-center">
              <Upload className="w-5 h-5 text-bordo" />
            </div>
            <h2 className="font-display text-xl sm:text-2xl font-bold text-tinta leading-tight">
              Importar <span className="italic text-bordo">participantes</span>
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-creme-light rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-tinta-soft" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-4">
          {/* Instructions */}
          <div className="bg-creme-light border border-tinta/10 rounded-lg p-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-tinta mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4 text-bordo" />
              Formato do arquivo
            </h3>
            <ul className="text-sm text-tinta-soft space-y-1 ml-6 list-disc">
              <li>Arquivo deve ser no formato CSV</li>
              <li>Colunas obrigatórias: <strong className="text-tinta">nome</strong> e <strong className="text-tinta">whatsapp</strong></li>
              <li>Coluna opcional: <strong className="text-tinta">data_nascimento</strong></li>
              <li>Formatos aceitos de data: DD/MM/YYYY, DD-MM-YYYY ou YYYY-MM-DD</li>
              <li>WhatsApp pode incluir ou não o código do país (55)</li>
            </ul>
            <button
              onClick={downloadTemplate}
              className="mt-3 inline-flex items-center gap-2 text-sm text-bordo hover:text-bordo-dark font-bold transition-colors"
            >
              <Download className="w-4 h-4" />
              Baixar modelo de exemplo
            </button>
          </div>

          {/* Upload Area */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-tinta/20 rounded-lg p-6 text-center cursor-pointer hover:border-bordo/50 hover:bg-bordo/5 transition-colors"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
            />

            {file ? (
              <div className="flex flex-col items-center gap-2">
                <FileText className="w-12 h-12 text-bordo" />
                <p className="text-sm font-bold text-tinta">{file.name}</p>
                <p className="text-xs text-tinta-soft italic">{(file.size / 1024).toFixed(2)} KB</p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                  className="text-xs text-bordo hover:text-bordo-dark font-semibold mt-2 transition-colors"
                >
                  Remover arquivo
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="w-12 h-12 text-tinta-soft/60" />
                <p className="text-sm font-bold text-tinta">
                  Clique ou arraste o arquivo CSV aqui
                </p>
                <p className="text-xs text-tinta-soft italic">Apenas arquivos .csv</p>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-bordo/8 border border-bordo/30 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-bordo flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-bordo">Erro ao importar</p>
                <p className="text-sm text-tinta mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Success Result */}
          {result && (
            <div className="bg-musgo/10 border border-musgo/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-musgo flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs font-bold uppercase tracking-wider text-musgo-dark">{result.message}</p>
                  <div className="mt-2 text-sm text-tinta space-y-1">
                    <p className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-musgo" />
                      <span><strong className="text-tinta">{result.imported}</strong> participante(s) importado(s)</span>
                    </p>
                    {result.duplicates > 0 && (
                      <p className="flex items-center gap-2 text-mostarda-dark">
                        <AlertTriangle className="w-4 h-4" />
                        <span><strong>{result.duplicates}</strong> duplicado(s) ignorado(s)</span>
                      </p>
                    )}
                    {result.errors && result.errors.length > 0 && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-bordo font-bold text-xs uppercase tracking-wider">
                          {result.errors.length} erro(s) encontrado(s)
                        </summary>
                        <ul className="mt-2 ml-4 space-y-1 text-xs text-tinta-soft italic">
                          {result.errors.slice(0, 10).map((err: any, idx: number) => (
                            <li key={idx}>Linha {err.line}: {err.error}</li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-4 sm:p-6 border-t border-tinta/10 bg-creme-light">
          <button
            onClick={handleClose}
            disabled={uploading}
            className="px-4 py-2 border border-tinta/15 rounded-lg text-sm font-semibold text-tinta-soft hover:bg-white hover:text-tinta disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleUpload}
            disabled={!file || uploading || !!result}
            className="px-5 py-2.5 rounded-lg text-sm font-bold text-creme bg-bordo hover:bg-bordo-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-creme"></div>
                Importando...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Importar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CSVImportModal;
